import type { GovernancePolicyFormData } from '@experiences/interfaces';
import { Scope } from '@experiences/interfaces';
import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useIntl } from 'react-intl';
import useSWR from 'swr';

import {
    useResourceCatalogNameMap,
    useTagLogicText,
} from '../../../../../common/hooks/useResourceCatalogNameMap';
import { getOrgLogicalNameAndTenantName } from '../../../../../services/organization/TenantService';
import { useStepHooks } from '../../../../common/UiWizard';
import {
    ACCESS_DECISION_KEYS,
    ACTOR_IDENTITY_TRANSLATION_KEYS,
    ACTOR_IDENTITY_TYPE_PLURAL_NAME_KEYS,
    AUTOMATION_TRANSLATION_KEYS,
    AUTOMATION_TYPE_PLURAL_NAME_KEYS,
    computeTypeChipLabel,
    type DisplayItem,
    getTagOperatorShortText,
    resolveDisplayItem,
    RESOURCE_TRANSLATION_KEYS,
    RESOURCE_TYPE_PLURAL_NAME_KEYS,
} from '../../governanceConstants';
import { collectEntityKeysFromSelectorsAndExecutableRule } from '../common/ResourceCatalogUtils';
import { useActorRuleNameResolution } from '../common/useActorRuleNameResolution';

interface ResourcesDisplay {
    items: DisplayItem[];
    typeChips: string[];
    tags: string[];
    tagLogicText: string;
    tagOperatorShort: string;
}

interface AutomationsDisplay {
    items: DisplayItem[];
    typeChips: string[];
    tags: string[];
    tagLogicText: string;
    tagOperatorShort: string;
}

const useGovernancePolicyCreatePolicyDefinitionStepViewModel = () => {
    const { formatMessage: translate } = useIntl();
    const { watch } = useFormContext<GovernancePolicyFormData>();

    const enforcement = watch('enforcement');
    const selectors = watch('selectors');
    const executableRule = watch('executableRule');
    const actorRule = watch('actorRule');
    const tenantId = watch('tenantId');

    const scope = watch('scope');
    const isOrganizationScope = scope === Scope.Organization;

    // Check if Resources and Automations are selected
    const isResourcesEmpty = !selectors || selectors.length === 0;
    // Actor process rule is required; actor identity rule is optional
    const isAutomationsEmpty = !executableRule?.values || executableRule.values.length === 0;
    const disableNext = isResourcesEmpty || isAutomationsEmpty;

    // Disable Next button if Resources or Automations are not selected
    useStepHooks({ disableNext });

    // Resolve tenant name from tenantId (needed for Resource Catalog calls)
    const { data: orgTenantInfo } = useSWR(
        tenantId ? tenantId : null,
        getOrgLogicalNameAndTenantName,
    );
    const tenantName = orgTenantInfo?.tenantName;
    const getTagLogicText = useTagLogicText();

    // Resolve actor identity and group names from services (fallback for missing valueNames)
    const {
        actorIdentityNameMap, groupNameMap: resolvedGroupNameMap,
    } = useActorRuleNameResolution(actorRule);

    // Collect all concrete entity keys (excluding wildcard-only "*") used in resources and automations
    const allEntityKeys = useMemo(
        () => collectEntityKeysFromSelectorsAndExecutableRule(selectors, executableRule),
        [ selectors, executableRule ],
    );

    const {
        idToNameMap,
        isLoadingNames,
    } = useResourceCatalogNameMap(
        tenantName,
        allEntityKeys,
        'resourceCatalogNamesByKeys',
    );

    // Format resources display
    const resourcesDisplay = useMemo<ResourcesDisplay | null>(() => {
        const currentSelectors = selectors ?? [];
        if (!currentSelectors || currentSelectors.length === 0) {
            return null;
        }

        const excludeText = translate({ id: 'CLIENT_GOVERNANCE_POLICY_DEFINITION_CHIP_EXCLUDE' });
        const valueNameMap = idToNameMap ?? {};
        const items: DisplayItem[] = [];
        const typeChips: string[] = [];

        currentSelectors.forEach((selector) => {
            const isAll = selector.values.length === 1 && selector.values[0] === '*';
            const pluralNameKey = RESOURCE_TYPE_PLURAL_NAME_KEYS[selector.resourceType];
            const pluralName = pluralNameKey ? translate({ id: pluralNameKey }) : selector.resourceType;

            typeChips.push(computeTypeChipLabel(isAll, selector.operator, pluralName, selector.values.length, excludeText));

            const item = resolveDisplayItem(selector, selector.resourceType, RESOURCE_TRANSLATION_KEYS, translate, valueNameMap);
            if (item) {
                items.push(item);
            }
        });

        // Get tags from first selector (tags are shared across all selectors)
        const firstSelector = currentSelectors[0];
        const tags = firstSelector?.tags?.values ?? [];
        const tagOperator = firstSelector?.tags?.operator ?? 'Or';

        return {
            items,
            typeChips,
            tags,
            tagLogicText: getTagLogicText(tagOperator),
            tagOperatorShort: getTagOperatorShortText(tagOperator, translate),
        };
    }, [ selectors, translate, idToNameMap, getTagLogicText ]);

    // Format automations display (actor processes + actor identities including Groups)
    const automationsDisplay = useMemo<AutomationsDisplay | null>(() => {
        const hasExecutables = executableRule?.values && executableRule.values.length > 0;
        const hasActorIdentities = actorRule?.values && actorRule.values.length > 0;

        if (!hasExecutables && !hasActorIdentities) {
            return null;
        }

        const excludeText = translate({ id: 'CLIENT_GOVERNANCE_POLICY_DEFINITION_CHIP_EXCLUDE' });
        const valueNameMap = idToNameMap ?? {};
        const items: DisplayItem[] = [];
        const typeChips: string[] = [];

        // Actor process chips
        if (hasExecutables) {
            executableRule.values.forEach((value) => {
                const isAll = value.values.length === 1 && value.values[0] === '*';
                const pluralNameKey = AUTOMATION_TYPE_PLURAL_NAME_KEYS[value.type];
                const pluralName = pluralNameKey ? translate({ id: pluralNameKey }) : value.type;

                typeChips.push(computeTypeChipLabel(isAll, value.operator, pluralName, value.values.length, excludeText));

                const item = resolveDisplayItem(value, value.type, AUTOMATION_TRANSLATION_KEYS, translate, valueNameMap);
                if (item) {
                    items.push(item);
                }
            });
        }

        // Actor identity chips (including Groups — ActorType.Group entries in actorRule.values)
        if (hasActorIdentities) {
            actorRule.values.forEach((value) => {
                const isAll = value.values.length === 1 && value.values[0] === '*';
                const pluralNameKey = ACTOR_IDENTITY_TYPE_PLURAL_NAME_KEYS[value.type];
                const pluralName = pluralNameKey ? translate({ id: pluralNameKey }) : value.type;

                typeChips.push(computeTypeChipLabel(isAll, value.operator, pluralName, value.values.length, excludeText));

                // Merge resolved names with form valueNames (resolved takes precedence as fallback)
                const mergedNameMap = {
                    ...actorIdentityNameMap,
                    ...resolvedGroupNameMap,
                    ...(value.valueNames ?? {}),
                };
                const item = resolveDisplayItem(value, value.type, ACTOR_IDENTITY_TRANSLATION_KEYS, translate, mergedNameMap);
                if (item) {
                    items.push(item);
                }
            });
        }

        // Get tags from executableRule
        const tags = executableRule?.tags?.values ?? [];
        const tagOperator = executableRule?.tags?.operator ?? 'Or';

        return {
            items,
            typeChips,
            tags,
            tagLogicText: getTagLogicText(tagOperator),
            tagOperatorShort: getTagOperatorShortText(tagOperator, translate),
        };
    }, [ executableRule, actorRule, translate, idToNameMap, getTagLogicText, actorIdentityNameMap, resolvedGroupNameMap ]);

    // Format access decision display
    const accessDecisionDisplay = useMemo<string | null>(() => {
        if (!enforcement) {
            return null;
        }
        const translationKey = ACCESS_DECISION_KEYS[enforcement];
        return translationKey ? translate({ id: translationKey }) : null;
    }, [ enforcement, translate ]);

    return {
        // Display data
        resourcesDisplay,
        automationsDisplay,
        accessDecisionDisplay,
        enforcement,
        // Validation
        isResourcesEmpty,
        isAutomationsEmpty,
        isLoadingNames,
        isOrganizationScope,
    };
};

export default useGovernancePolicyCreatePolicyDefinitionStepViewModel;
